import { useEffect, useState, useContext } from 'react';
import { message } from '@brokerbay/design-components';
import { getJpmErrorCodes } from 'api/SupraController';
import { SupraController } from 'api';
import { AuthContext } from 'contexts';

type UseJpmcSessionForCardUpdateArgs = {
  modalVisible: boolean;
  cardholderName?: string;
};

type UseJpmcSessionForCardUpdateReturn = {
  /** The JPMC session ID for the card update. */
  jpmcSessionId: string;
  /** Indicates if the JPMC session is currently being loaded. */
  isLoadingJPMCSession: boolean;
  /** A map of JPMC error codes to their corresponding messages. */
  jpmErrorMap: Record<string, string>;
  /** Resets the JPMC session state. */
  resetJpmcSession: () => void;
};

/** Hook to manage JPMC session for card update.
 * Fetches a new session ID when the modal becomes visible.
 */
export const useJpmcSessionForCardUpdate = ({
  modalVisible,
  cardholderName,
}: UseJpmcSessionForCardUpdateArgs): UseJpmcSessionForCardUpdateReturn => {
  const { auth } = useContext(AuthContext);
  const [jpmErrorMap, setJpmErrorMap] = useState<Record<string, string>>({});
  const [jpmcSessionId, setJPMCSessionId] = useState<string>('');
  const [isLoadingJPMCSession, setIsLoadingJPMCSession] =
    useState<boolean>(true);

  /** Resets the JPMC session state. */
  const resetJpmcSession = () => {
    setJPMCSessionId('');
    setIsLoadingJPMCSession(true);
  };

  useEffect(() => {
    if (!modalVisible) {
      resetJpmcSession();
    }
  }, [modalVisible]);

  useEffect(() => {
    if (!modalVisible || !isLoadingJPMCSession) return;

    let isMounted = true;

    const loadJpmcSession = async () => {
      try {
        const cssUrl = process.env.JPMC_IFRAME_CSS;

        const resolvedCardholderName =
          cardholderName || `${auth.firstName} ${auth.lastName}`;

        const sessionId = await SupraController.getJpmUidKeyholder({
          cssUrl,
          cardholderName: resolvedCardholderName,
        });

        const errorMap = await getJpmErrorCodes();

        if (!isMounted) return;

        setJPMCSessionId(sessionId);
        setJpmErrorMap(errorMap);
      } catch (e) {
        if (isMounted) {
          message.error(
            'Unable to start Chase card session. Please try again.',
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingJPMCSession(false);
        }
      }
    };

    loadJpmcSession();

    return () => {
      isMounted = false;
    };
  }, [
    modalVisible,
    isLoadingJPMCSession,
    cardholderName,
    auth.firstName,
    auth.lastName,
  ]);

  return {
    jpmErrorMap,
    jpmcSessionId,
    isLoadingJPMCSession,
    resetJpmcSession,
  };
};
